import { useState } from 'react';

interface FinalConfirmationModalProps {
  userRole: 'buyer' | 'seller';
  userData: {
    id: string;
    email: string;
    name: string;
  } | null;
  onConfirm: () => void;
  onCancel: () => void;
  onBack: () => void;
  isDeleting: boolean;
  error: string;
}

export function FinalConfirmationModal({
  userRole,
  userData,
  onConfirm,
  onCancel,
  onBack,
  isDeleting,
  error
}: FinalConfirmationModalProps) {
  const [confirmText, setConfirmText] = useState('');
  const [agreed, setAgreed] = useState(false);

  const expectedConfirmText = '계정삭제';
  const isConfirmValid = confirmText === expectedConfirmText && agreed;

  const getUserRoleText = () => {
    return userRole === 'buyer' ? '구매자' : '판매자';
  };

  const handleConfirmTextChange = (value: string) => {
    setConfirmText(value);
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isConfirmValid && !isDeleting) {
      onConfirm();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* 스크롤 가능한 콘텐츠 영역 */}
      <div className="flex-1 overflow-y-auto">
        {/* 최종 경고 아이콘 */}
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </div>
      </div>

      {/* 제목 */}
      <div className="text-center mb-6">
        <h4 className="text-lg font-bold text-red-600 mb-2">
          최종 확인
        </h4>
        <p className="text-sm text-gray-600">
          이 작업은 되돌릴 수 없습니다
        </p>
      </div>

      {/* 사용자 정보 확인 */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
        <h5 className="text-sm font-medium text-gray-900 mb-3">삭제될 계정 정보</h5>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">이름:</span>
            <span className="font-medium text-gray-900">{userData?.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">이메일:</span>
            <span className="font-medium text-gray-900">{userData?.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">계정 유형:</span>
            <span className="font-medium text-gray-900">{getUserRoleText()}</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* 확인 문구 입력 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            계정 삭제를 확인하려면 <span className="font-bold text-red-600">"{expectedConfirmText}"</span>를 입력하세요
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => handleConfirmTextChange(e.target.value)}
            placeholder="계정삭제"
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
              confirmText.length > 0 && confirmText !== expectedConfirmText 
                ? 'border-red-300 bg-red-50' 
                : 'border-gray-300'
            }`}
            disabled={isDeleting}
          />
          {confirmText.length > 0 && confirmText !== expectedConfirmText && (
            <p className="text-xs text-red-600 mt-1">
              정확히 "{expectedConfirmText}"를 입력해주세요
            </p>
          )}
        </div>

        {/* 동의 체크박스 */}
        <div className="space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={isDeleting}
              className="mt-1 w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
            />
            <div className="text-sm text-gray-700">
              <p className="font-medium mb-1">다음 사항을 이해하고 동의합니다:</p>
              <ul className="text-xs space-y-1 text-gray-600">
                <li>• 삭제된 계정과 모든 개인정보는 복구할 수 없습니다</li>
                <li>• 이 작업은 즉시 실행되며 취소할 수 없습니다</li>
                <li>• 주문 관련 데이터는 법적 요구사항에 따라 보존됩니다</li>
                <li>• 계정 삭제로 인한 손실에 대해 책임을 지지 않습니다</li>
              </ul>
            </div>
          </label>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* 최종 경고 */}
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
          <p className="text-xs text-red-700 text-center font-medium">
            ⚠️ 이 작업은 되돌릴 수 없습니다. 신중하게 결정해주세요.
          </p>
        </div>
      </form>
      </div>

      {/* 고정된 버튼 영역 */}
      <div className="border-t border-gray-200 p-4 bg-white">
        {/* 버튼들 */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={isDeleting}
            className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            이전
          </button>
          
          <button
            type="button"
            onClick={onCancel}
            disabled={isDeleting}
            className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            취소
          </button>
          
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isConfirmValid || isDeleting}
            className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              !isDeleting && isConfirmValid
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500'
            }`}
          >
            {isDeleting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                삭제 중...
              </div>
            ) : (
              '계정 삭제'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}